// useCompaniesWithGlobalFilters.ts
import { useMemo } from "react";
import { useFilterSettingsStore, useLocationStore, usePipedriveStore } from "..";
import { CompanyMinLocationCount } from "../interfaces/IFilterSettings";
import IPipedriveOrganization from "../interfaces/IPipedriveOrganization";

export default function useCompaniesWithGlobalFilters(companies: IPipedriveOrganization[]): IPipedriveOrganization[] {
    const { filters: { companySettings: companyFilters }, } = useFilterSettingsStore();
    const locationStore = useLocationStore();
    const pipedriveStore = usePipedriveStore();

    const logoCompanyIds = useMemo(() => new Set((pipedriveStore.organizationsLogos).map(l => l.organizationId)), [pipedriveStore.organizationsLogos]);
    const companiesLocationCounts = useMemo(() => {
        const map = new Map<number, number>();
        for (const { pipedriveCompanyId } of locationStore.locations) {
            if (pipedriveCompanyId == null) continue;
            map.set(pipedriveCompanyId, (map.get(pipedriveCompanyId) ?? 0) + 1);
        }
        return map;
    }, [locationStore.locations]);

    const companiesWithLogo = useMemo(() => {
        if (!companyFilters.hideNoLogoCompany) return companies;
        return companies.filter(c => logoCompanyIds.has(c.id));
    }, [companies, companyFilters.hideNoLogoCompany, logoCompanyIds]);

    return useMemo(() => {
        const minLocationNumber = getNumberLocation(companyFilters.minLocationCount);
        if (minLocationNumber === 0) return companiesWithLogo;
        return companiesWithLogo.filter(c => (companiesLocationCounts.get(c.id) ?? 0) >= minLocationNumber);
    }, [companiesWithLogo, companyFilters.minLocationCount, companiesLocationCounts]);
}

const getNumberLocation = (locationCount: CompanyMinLocationCount): number => {
    switch (locationCount) {
        case CompanyMinLocationCount.All:
            return 0;
        case CompanyMinLocationCount.Ten:
            return 10;
        case CompanyMinLocationCount.TwentyFive:
            return 25;
        case CompanyMinLocationCount.Fifty:
            return 50;
        case CompanyMinLocationCount.OneHundred:
            return 100;
    }
}